import React from 'react'

import { Button, Checkbox, TextInput } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import RichEditor from 'Pods/Shared/RichEditor/RichEditor'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import DeadlineField from './Components/DeadlineField/DeadlineField'
import SoftwareField from './Components/SoftwareField/SoftwareField'
import VisualReferences from './Components/VisualReferences/VisualReferences'
import useAssistForm, { BUDGET_DEFAULT } from './Hooks/useAssistForm'

function AssistForm({ onSuccess }) {
  const t = useTranslation()
  const {
    data,
    softwareOptions,
    showErrors,
    isSubmitting,
    errorFor,
    handleFieldChange,
    handleDescriptionChange,
    handleSoftwareChange,
    handleDayChange,
    handleEndlessChange,
    handleReferencesChange,
    handleResellChange,
    handleTermsChange,
    handleSubmit,
  } = useAssistForm(onSuccess)

  const termsLabel = (
    <span>
      {t('viewerSidebar.enhance.assist.form.terms.agree')}
      <a href="/pages/terms-and-conditions" target="_blank" rel="noopener noreferrer">
        {t('viewerSidebar.enhance.assist.form.terms.link')}
      </a>
      <b className="cgt-legacy-color-utility-danger-100"> *</b>
    </span>
  )

  return (
    <form
      className="assist-tab"
      onSubmit={handleSubmit}
      autoComplete="off"
      noValidate
    >
      <div className="assist-tab__fields">
        <span className="cgt-heading-50">
          {t('viewerSidebar.enhance.assist.heading')}
        </span>

        <TextInput
          id="assist-title"
          name="title"
          label={t('viewerSidebar.enhance.assist.form.title.label')}
          size="small"
          placeholder={t('viewerSidebar.enhance.assist.form.title.placeholder')}
          value={data.title}
          onChange={handleFieldChange}
          isWarning={!!errorFor('title')}
          helpText={errorFor('title') || undefined}
          isPilot
          isRequired
          textInputClassName="assist-tab__input"
        />

        <RichEditor
          label={t('viewerSidebar.enhance.assist.form.description')}
          required
          value={data.description}
          onChange={handleDescriptionChange}
          showError={showErrors}
          errorText={errorFor('description')}
          useNewStyles
          minHeight={120}
          labelClassName="cgt-body-base-semibold"
        />

        <SoftwareField
          options={softwareOptions}
          selected={data.software}
          onChange={handleSoftwareChange}
          errorText={errorFor('software')}
        />

        <VisualReferences onChange={handleReferencesChange} />

        <div>
          <div className="assist-tab__row">
            <DeadlineField
              deadline={data.deadline}
              isEndless={data.isEndless}
              onDayChange={handleDayChange}
              onEndlessChange={handleEndlessChange}
              errorText={errorFor('deadline')}
            />

            <TextInput
              id="assist-budget"
              name="budget"
              label={t('viewerSidebar.enhance.assist.form.budget')}
              inputType="number"
              isCurrency
              step="1"
              placeholder={`${BUDGET_DEFAULT}`}
              value={data.budget}
              onChange={handleFieldChange}
              isWarning={!!errorFor('budget')}
              helpText={errorFor('budget') || undefined}
              isPilot
              isRequired
              size="small"
              wrapperClassName="assist-tab__budget"
              textInputClassName="assist-tab__input"
            />
          </div>

          <div className="assist-tab__agreement">
            <Checkbox
              id="assist-resell"
              name="resell"
              size="medium"
              checkedColor="primary"
              isControlled
              isChecked={data.resell}
              onChange={handleResellChange}
              label={t('viewerSidebar.enhance.assist.form.resell')}
              className="assist-tab__checkbox"
            />
            <Checkbox
              id="assist-terms"
              name="terms"
              size="medium"
              checkedColor="primary"
              isControlled
              isChecked={data.terms}
              onChange={handleTermsChange}
              label={termsLabel}
              className="assist-tab__checkbox"
            />
          </div>
        </div>
      </div>

      <div className="assist-tab__footer">
        <Button
          type="primary"
          size="small"
          actionType="submit"
          expand
          disabled={!data.terms || isSubmitting}
          customClass="assist-tab__submit-button"
        >
          {t('viewerSidebar.enhance.assist.form.submit')}
        </Button>
      </div>
    </form>
  )
}

AssistForm.propTypes = {
  onSuccess: func.isRequired,
}

export default AssistForm
